import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import SupportModal from './SupportModal';
import { isAuthenticated } from '../services/auth';
import type { Theme } from '../types';
import {
  Home,
  Library,
  GraduationCap,
  Settings,
  Wand2,
  Menu,
  X,
  BrainCircuit,
  Heart,
  Shield,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

interface AppLayoutProps {
  children: React.ReactNode;
  theme: Theme;
  onThemeChange: (theme: Theme) => void;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  theme,
  onThemeChange,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [connected, setConnected] = useState(isAuthenticated());
  const location = useLocation();

  useEffect(() => {
    const checkAuth = () => setConnected(isAuthenticated());
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const navItems = [
    { to: '/', icon: Home, label: 'Home' },
    { to: '/decks', icon: Library, label: 'Decks' },
    { to: '/study', icon: GraduationCap, label: 'Study' },
    { to: '/generate', icon: Wand2, label: 'Generate' },
    { to: '/settings', icon: Settings, label: 'Settings' },
    { to: '/admin', icon: Shield, label: 'Admin' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-50 flex items-center justify-between px-4">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <div className="flex items-center gap-2">
          <BrainCircuit className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          <span className="font-bold text-lg">FlashAI</span>
        </div>
        <div className="w-10" />
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-0 left-0 h-screen w-64 shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-40 transition-transform duration-300 lg:translate-x-0 ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Logo */}
          <div className="hidden lg:flex items-center gap-3 p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <BrainCircuit className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-gray-900 dark:text-gray-100">FlashAI</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Smart Flashcards</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? 'text-blue-600 dark:text-blue-400' : ''}`} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <button
              onClick={() => {
                setIsSupportOpen(true);
                setIsSidebarOpen(false);
              }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
            >
              <Heart className="w-5 h-5 text-pink-500" />
              <span>Support</span>
            </button>
          </nav>

          {/* Footer */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
            {/* Connection Status */}
            {connected ? (
              <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                <CheckCircle className="w-4 h-4" />
                <span>Pollinations Connected</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs text-yellow-600 dark:text-yellow-400">
                <AlertCircle className="w-4 h-4" />
                <span>Not Connected</span>
              </div>
            )}
            <ThemeToggle
              theme={theme}
              onChange={onThemeChange}
              variant="button"
              className="w-full justify-start"
            />
            <a
              href="https://pollinations.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <img
                src="https://img.shields.io/badge/Powered%20with-Pollinations-8a2be2?style=for-the-badge&logoColor=white&labelColor=6a0dad"
                alt="Powered with Pollinations.ai"
                className="h-8 w-auto"
              />
            </a>
          </div>
        </aside>

        {/* Mobile overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-h-screen pt-16 lg:pt-0">
          <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">{children}</div>
          {/* Footer Badge */}
          <div className="flex justify-center pb-6">
            <a
              href="https://pollinations.ai"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://img.shields.io/badge/Powered%20with-Pollinations-8a2be2?style=for-the-badge&logoColor=white&labelColor=6a0dad"
                alt="Powered with Pollinations.ai"
                className="h-8 w-auto"
              />
            </a>
          </div>
        </main>
      </div>

      <SupportModal isOpen={isSupportOpen} onClose={() => setIsSupportOpen(false)} />
    </div>
  );
};

export default AppLayout;
